// src/components/Codebook/CodebookNarrativeBlock.tsx
// Renders narrative content with simple markdown-like formatting

import React from 'react';

interface CodebookNarrativeBlockProps {
  content: string;
}

const CodebookNarrativeBlock: React.FC<CodebookNarrativeBlockProps> = ({ content }) => {
  // Simple markdown-like parser
  const renderMarkdown = (text: string) => {
    const lines = text.split('\n');
    const elements: React.ReactNode[] = [];
    let i = 0;

    while (i < lines.length) {
      const line = lines[i];

      // Headers
      if (line.startsWith('### ')) {
        elements.push(
          <h3 key={i} className="text-xl font-semibold mt-5 mb-2">
            {line.substring(4)}
          </h3>
        );
      } else if (line.startsWith('## ')) {
        elements.push(
          <h2 key={i} className="text-2xl font-bold mt-6 mb-3">
            {line.substring(3)}
          </h2>
        );
      } else if (line.startsWith('# ')) {
        elements.push(
          <h1 key={i} className="text-3xl font-bold mt-6 mb-4">
            {line.substring(2)}
          </h1>
        );
      }
      // Bold and italic
      else if (line.trim() === '') {
        elements.push(<div key={i} className="h-2" />);
      }
      // Lists
      else if (line.trim().startsWith('- ')) {
        elements.push(
          <ul key={i} className="list-disc list-inside space-y-2 mb-4 ml-2">
            {lines.slice(i).map((l, idx) => {
              if (!l.trim().startsWith('- ')) return null;
              return (
                <li key={idx} className="text-base leading-7">
                  {l.trim().substring(2)}
                </li>
              );
            })}
          </ul>
        );
        // Skip list lines
        while (i < lines.length && lines[i].trim().startsWith('- ')) {
          i++;
        }
        i--;
      }
      // Regular paragraph
      else if (line.trim()) {
        // Replace inline markdown: **bold** -> <b>, *italic* -> <i>, `code` -> <code>
        const processedLine = line
          .replace(/\*\*([^*]+)\*\*/g, '<b>$1</b>')
          .replace(/\*([^*]+)\*/g, '<i>$1</i>')
          .replace(/`([^`]+)`/g, '<code>$1</code>');

        elements.push(
          <p
            key={i}
            className="text-base leading-7 mb-4"
            dangerouslySetInnerHTML={{
              __html: processedLine
                .replace(/<b>/g, '<span class="font-bold">')
                .replace(/<\/b>/g, '</span>')
                .replace(/<i>/g, '<span class="italic">')
                .replace(/<\/i>/g, '</span>')
                .replace(/<code>/g, '<code class="bg-card/50 px-1 rounded text-sm font-mono">')
            }}
          />
        );
      }

      i++;
    }

    return elements;
  };

  return (
    <div className="prose max-w-none text-foreground space-y-4 font-serif">
      {renderMarkdown(content)}
    </div>
  );
};

export default CodebookNarrativeBlock;
